/**
 * @fileoverview Generic Alert Banner Component
 * @description Reusable component for displaying alert banners in the application.
 * Can be used for any type of alert: warnings, errors, info, success messages.
 *
 * @example
 * // Single alert
 * <AlertBanner
 *   alerts={[{
 *     id: "maintenance",
 *     severity: "warning",
 *     message: "System maintenance scheduled for tonight.",
 *     dismissible: true
 *   }]}
 * />
 *
 * @example
 * // Multiple alerts with links
 * <AlertBanner
 *   alerts={[
 *     {
 *       id: "institution-pending",
 *       severity: "warning",
 *       title: "Institutions pending:",
 *       message: "Institution A, Institution B",
 *       link: { to: "/settings", label: "View details" }
 *     },
 *     {
 *       id: "docs-expired",
 *       severity: "error",
 *       message: "Documents have expired."
 *     }
 *   ]}
 * />
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Alert, Stack, IconButton, Box } from "@mui/material";
import { intl } from "dmsbasereact";
import CloseIcon from '@mui/icons-material/Close';

const SESSION_STORAGE_PREFIX = "alertBannerDismissed_";

/**
 * AlertBanner - Generic alert banner component
 * @param {Object} props - Component props
 * @param {Array} props.alerts - Array of alert objects to display
 * @param {number} props.spacing - Spacing between alerts (default: 1)
 */
export const AlertBanner = ({ alerts = [], spacing = 1 }) => {
  const [dismissedAlerts, setDismissedAlerts] = useState({});

  // Load dismissed state from sessionStorage on mount
  useEffect(() => {
    const dismissed = {};
    alerts.forEach(alert => {
      if (alert.dismissible && alert.id) {
        const key = `${SESSION_STORAGE_PREFIX}${alert.id}`;
        dismissed[alert.id] = sessionStorage.getItem(key) === "true";
      }
    });
    setDismissedAlerts(dismissed);
  }, [alerts]);

  const handleDismiss = useCallback((alertId) => {
    const key = `${SESSION_STORAGE_PREFIX}${alertId}`;
    sessionStorage.setItem(key, "true");
    setDismissedAlerts(prev => ({ ...prev, [alertId]: true }));
  }, []);

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => {
    if (!alert.dismissible) return true;
    return !dismissedAlerts[alert.id];
  });

  if (visibleAlerts.length === 0) {
    return null;
  }

  return (
    <Stack spacing={spacing} sx={{ mb: 2 }}>
      {visibleAlerts.map((alert, index) => (
        <Alert
          key={alert.id || index}
          severity={alert.severity || "info"}
          icon={alert.icon}
          action={
            alert.dismissible ? (
              <IconButton
                aria-label={intl.get("Close").d("Close")}
                color="inherit"
                size="small"
                onClick={() => handleDismiss(alert.id)}
                disableRipple
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  padding: 0,
                  borderRadius: "4px",
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : undefined
          }
          sx={{ borderRadius: 1, ...alert.sx }}
        >
          <Box>
            {alert.title && <strong>{alert.title}</strong>}
            {alert.title && alert.message && ' '}
            {alert.message}
            {alert.link && (
              <>
                {' '}
                <Link
                  to={alert.link.to}
                  style={{ color: 'inherit', fontWeight: 600 }}
                >
                  {alert.link.label}
                </Link>
              </>
            )}
            {alert.children}
          </Box>
        </Alert>
      ))}
    </Stack>
  );
};

AlertBanner.propTypes = {
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      severity: PropTypes.oneOf(["error", "warning", "info", "success"]),
      title: PropTypes.string,
      message: PropTypes.node,
      icon: PropTypes.node,
      dismissible: PropTypes.bool,
      link: PropTypes.shape({
        to: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
      children: PropTypes.node,
      sx: PropTypes.object,
    })
  ),
  spacing: PropTypes.number,
};

export default AlertBanner;
